import { getCachedCapacity } from '@/lib/accountCapabilities';

// ─────────────────────────────────────────────────────────────
// ZIFEK — Capacités du site public selon le type de compte
// Règle métier :
//   • type « Boutique »            → produits uniquement
//   • type « Services »            → services uniquement
//   • type « Mixte »               → produits ET services
//   • type « Restauration »        → menu (produits) + affichage resto
//   • type « Marketplace … »       → agrège les contenus des vendeurs
//   • tout autre type              → comportement adaptatif (selon le contenu réel)
// ─────────────────────────────────────────────────────────────

/** Id de la ligne « Restauration » dans la table typecompte. */
export const RESTAURANT_TYPE_COMPTE = 2;

/**
 * Id de la ligne « Client » dans la table typecompte.
 * Sert à décider (QR, réseaux, filtres) sans jamais se baser sur le libellé,
 * car un libellé peut être renommé sans que l'ID ne change.
 */
export const TYPE_COMPTE_CLIENT = 6;

export interface StoreCapabilities {
  /** Le site public peut-il montrer des produits ? */
  products: boolean;
  /** Le site public peut-il montrer des services ? */
  services: boolean;
  /** S'agit-il d'un commerce de restauration (menu + horaires) ? */
  restaurant: boolean;
  /** S'agit-il d'un compte marchand de type marketplace (multi-vendeurs) ? */
  marketplace: boolean;
}

// Ids de type de compte (table `typecompte`) orientés PRODUITS / SERVICES.
// Permet une détection fiable même quand `users.type` n'est pas un libellé
// explicite (ex. « Mode et couture ») ou contient un identifiant numérique.
const PRODUCT_TYPE_IDS = [3, 5, 11, 13]; // Boutique, Mode et couture, Beauté, Marketplace produits
const SERVICE_TYPE_IDS = [4, 10, 12, 14]; // Services, Salon de coiffure, Expédition colis, Marketplace services
const MIXED_TYPE_IDS = [7];
const MARKETPLACE_TYPE_IDS = [13, 14, 15];

export function getStoreCapabilities(
  type: string | null | undefined,
  typecompte?: number | null,
): StoreCapabilities {
  const t = (type || '').trim().toLowerCase();
  const id = Number(typecompte);
  // Certains comptes stockent un identifiant numérique dans le champ `type`.
  const numericType = /^\d+$/.test(t) ? Number(t) : null;
  const effectiveId = Number.isFinite(id) && id > 0 ? id : numericType;

  // Capacité officielle du type de compte (table `typecompte`) : source de vérité.
  const cap = effectiveId != null ? getCachedCapacity(effectiveId) : null;

  // Marketplace : identifié par l'id de type de compte, les capacités OU le libellé.
  const marketplace =
    t.includes('marketplace') ||
    t.includes('market place') ||
    cap === 'marketplace' ||
    (effectiveId != null && MARKETPLACE_TYPE_IDS.includes(effectiveId));

  // 1) Capacités pilotées par la table `typecompte` (aucune liste d'ID en dur).
  if (cap && cap !== 'autre' && cap !== 'client') {
    switch (cap) {
      case 'restauration':
        return { products: true, services: false, restaurant: true, marketplace };
      case 'produits':
        return { products: true, services: false, restaurant: false, marketplace };
      case 'services':
        return { products: false, services: true, restaurant: false, marketplace };
      case 'mixte':
        return { products: true, services: true, restaurant: false, marketplace };
      case 'marketplace':
        return { products: true, services: true, restaurant: false, marketplace: true };
      default:
        break;
    }
  }

  // 2) Repli historique (libellés / identifiants) tant que le cache n'est pas chargé.
  // Restauration : identifié par l'id de type de compte OU par le libellé.
  const isRestaurant =
    id === RESTAURANT_TYPE_COMPTE ||
    t.includes('restaur') ||
    t.includes('resto');
  if (isRestaurant) {
    return { products: true, services: false, restaurant: true, marketplace };
  }

  const hasMix = t.includes('mix') || (effectiveId != null && MIXED_TYPE_IDS.includes(effectiveId));
  if (hasMix) return { products: true, services: true, restaurant: false, marketplace };

  const isProductOnly =
    (effectiveId != null && PRODUCT_TYPE_IDS.includes(effectiveId)) || t.includes('boutique');
  const isServiceOnly =
    (effectiveId != null && SERVICE_TYPE_IDS.includes(effectiveId)) || t.includes('service');

  if (isProductOnly && !isServiceOnly) {
    return { products: true, services: false, restaurant: false, marketplace };
  }
  if (isServiceOnly && !isProductOnly) {
    return { products: false, services: true, restaurant: false, marketplace };
  }

  // Type inconnu (ou non listé) : on laisse le contenu décider.
  return { products: true, services: true, restaurant: false, marketplace };
}